import React, { JSX, useEffect, useRef, useState } from 'react'
import './index.scss'
import TabsAlertMessage from '@Components/Popups/TabsAlertMessage'
import { stringToInline } from '@App/helpers/string-to-inline'
import { useLocation, useNavigate } from 'react-router'
import ContentHeader from '@Components/ContentHeader'
import Tabs from '@Components/TabsWrapperComponent/Tabs'

export interface Tab {
    tabName: string
    tabDisplayName: React.ReactNode
    tabDescription?: JSX.Element
    tabComponent: React.ReactNode
}

interface TabsWrapperComponentProps {
    tabs: Tab[]
    defaultTab: Tab
    onTabChange?(tabName: string): void
    preventScrollReset?: boolean
    showContentHeaderPerTab?: boolean
}

const TabsWrapperComponent = (props: TabsWrapperComponentProps) => {
    const { tabs, defaultTab, onTabChange, preventScrollReset, showContentHeaderPerTab } = props
    const [activeTabName, setActiveTabName] = useState<string>(defaultTab.tabName)
    const [activeLineStyle, setActiveLineStyle] = useState<any>()
    const tabsContainerRef = useRef<HTMLDivElement>(null)
    const location = useLocation()
    const navigate = useNavigate()

    const onTab = (tabName: string) => {
        setActiveTabName(tabName)
        setUrl(tabName)
        if (onTabChange) {
            onTabChange(tabName)
        }
    }

    const setUrl = (tabName: string) => {
        const activeHash = '#' + stringToInline(tabName)
        const tabLocation = location.pathname + activeHash
        navigate(tabLocation, { replace: true, preventScrollReset: !!preventScrollReset })
    }

    const checkHash = () => {
        const hash = location.hash.split('#')[1]
        const currentTab = tabs.map((tab) => tab.tabName).find((name: string) => hash === stringToInline(name))
        if (currentTab) {
            setActiveTabName(currentTab)
            setActiveLineStyle(getActiveLineStyleForTabName(currentTab))
        } else {
            setUrl(defaultTab.tabName)
        }
    }

    useEffect(() => {
        checkHash()
    }, [location])

    const getActiveLineStyleForTabName = (tabName: string) => {
        const tabNamesLocal = tabs.map((tab) => tab.tabName)
        const startIndex = tabNamesLocal.findIndex((item: string) => item === tabName)
        const activeTab = tabsContainerRef.current && (tabsContainerRef.current.children[startIndex] as HTMLElement)
        const tabWidth = activeTab?.offsetWidth
        const tabLeft = activeTab?.offsetLeft
        return { transform: `translateX(${tabLeft}px)`, width: tabWidth }
    }

    return (
        <>
            <div className="head-block">
                {!!showContentHeaderPerTab && (
                    <ContentHeader
                        title={activeTabName}
                        descriptionElement={tabs.find((tab: Tab) => tab.tabName === activeTabName)?.tabDescription}
                    />
                )}
            </div>
            <div className="tabs-wrapper-component">
                <Tabs tabs={tabs} onTab={onTab} activeLineStyle={activeLineStyle} tabsContainerRef={tabsContainerRef} />
                <TabsAlertMessage currentTab={activeTabName} />
                <div className="tabs-content">
                    {tabs.find((tab: Tab) => tab.tabName === activeTabName)?.tabComponent}
                </div>
            </div>
        </>
    )
}

export default TabsWrapperComponent
